import type { SubsSection, SubsStoreState } from './types'

type ChildrenMap = SubsStoreState['childrenMap']

export interface LegacyTreeProjection {
  rootIds: Record<SubsSection, number[]>
  childrenMap: ChildrenMap
}

export function idsFromLegacyTreeItems(items: any): number[] {
  if (!Array.isArray(items)) return []
  return items
    .map((item: any) => item?.[0]?.[0])
    .filter((id: unknown, index: number, ids: unknown[]): id is number =>
      typeof id === 'number' && id > 0 && ids.indexOf(id) === index
    )
}

function mergeIds(previous: number[], next: number[]): number[] {
  return Array.from(new Set([...previous, ...next]))
}

export function mergeLegacyChildrenMaps(previous: ChildrenMap, next: ChildrenMap): ChildrenMap {
  const merged = { ...previous }
  for (const [parentKey, children] of Object.entries(next)) {
    const parentId = Number(parentKey)
    if (!(parentId > 0)) continue
    const current = merged[parentId] || { answers: [], submsges: [] }
    merged[parentId] = {
      answers: mergeIds(current.answers, children.answers),
      submsges: mergeIds(current.submsges, children.submsges),
    }
  }
  return merged
}

/**
 * Project the recursive legacy `[answers, submessages]` tree into the flat
 * parent-to-children map consumed by React row recursion.
 */
export function projectLegacySubsTree(subsTree: any): LegacyTreeProjection {
  const sections = Array.isArray(subsTree) ? subsTree : [[], []]
  const childrenMap: ChildrenMap = {}

  const visit = (items: any): void => {
    if (!Array.isArray(items)) return
    for (const item of items) {
      const parentId = item?.[0]?.[0]
      if (!(typeof parentId === 'number' && parentId > 0)) continue

      const answers = idsFromLegacyTreeItems(item?.[1])
      const submsges = idsFromLegacyTreeItems(item?.[2])
      if (answers.length || submsges.length) {
        childrenMap[parentId] = { answers, submsges }
      }

      visit(item?.[1])
      visit(item?.[2])
    }
  }

  visit(sections[0])
  visit(sections[1])

  return {
    rootIds: {
      answers: idsFromLegacyTreeItems(sections[0]),
      submsges: idsFromLegacyTreeItems(sections[1]),
    },
    childrenMap,
  }
}
